import type * as React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { useTranslation } from 'react-i18next';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import cloudscapeMessages from '@cloudscape-design/components/i18n/messages/all.all';
import { AuthProvider } from '@/auth/AuthProvider';
import { AppVersionGate } from '@/components/AppVersionGate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { InstallPrompt } from '@/components/InstallPrompt';
import { WebPushBootstrap } from '@/components/WebPushBootstrap';
import { initPostHog } from '@/lib/posthog';
import { trackPageview } from '@/lib/ga4';
import { initColorScheme } from '@/lib/theme';
import { router } from '@/routes/router';
import '@/lib/serverClock';
import '@/i18n';
import '@cloudscape-design/global-styles/index.css';
import './index.css';
import { renderAfterServerClock } from '@etiquetachef/ui-shared/online';

initPostHog();
initColorScheme();

// GA4 pageview por rota (stream admin G-Q0ZMBQ7Q4Y). O index.html usa
// send_page_view:false, então o boot e cada navegação SPA são reportados
// aqui — paridade com capture_pageview:true do PostHog, sem depender do
// Enhanced Measurement do painel. Dedup por path para não duplicar em
// re-renders/estados intermediários do router.
let lastTrackedPath = '';
/** Reporta o pageview do GA4 da rota atual, uma vez por path+search (#66). */
function trackCurrentRoute(): void {
  const { pathname, search } = router.state.location;
  const path = pathname + search;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  trackPageview(path);
}
trackCurrentRoute();
router.subscribe(trackCurrentRoute);

/**
 * Mapeia o idioma do react-i18next para um locale suportado pelo Cloudscape
 * (`messages/all.all` traz de/en/es/fr/it/pt-BR, entre outros). `de` desde a
 * #86 (2026-10-13).
 */
function cloudscapeLocale(lng: string): string {
  if (lng.startsWith('pt')) return 'pt-BR';
  if (lng.startsWith('es')) return 'es';
  if (lng.startsWith('it')) return 'it';
  if (lng.startsWith('fr')) return 'fr';
  if (lng.startsWith('de')) return 'de';
  return 'en';
}

/**
 * Fornece os textos de chrome dos componentes Cloudscape (PropertyFilter,
 * Pagination, Table, etc.) no idioma do admin, sem precisar passar
 * `i18nStrings` à mão em cada componente.
 */
function CloudscapeI18n({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { i18n } = useTranslation();
  return (
    <I18nProvider locale={cloudscapeLocale(i18n.language)} messages={[cloudscapeMessages]}>
      {children}
    </I18nProvider>
  );
}

/** `InstallPrompt` (PWA) com os textos traduzidos; a chave de storage separa o admin do app. */
function InstallPromptI18n(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <InstallPrompt
      title={t('common.install.title')}
      description={t('common.install.description')}
      actionLabel={t('common.install.action')}
      storageKey="etiquetachef:admin:install-prompt"
    />
  );
}

// React Query: padrão desde o scaffolding (4dbd8df25, 2026-04-26) — 30 s de
// staleTime, sem refetch no foco e uma só nova tentativa. Leitura do admin
// costuma ser listener (`onSnapshot`) ou callable cara; refazer a cada troca de
// aba só custaria leituras.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('admin: #root não encontrado em index.html');

// Ordem dos providers (de fora para dentro) importa:
// - ErrorBoundary por fora de tudo — erro em provider também vira tela de
//   erro com report, não tela branca;
// - AuthProvider antes do router — o guard `RequireSuperAdmin` lê `useAuth`;
// - AppVersionGate/WebPushBootstrap fora do RouterProvider, montados uma vez
//   no shell (não remontam a cada rota). O WebPushBootstrap NÃO consulta o
//   `useAuth`: roda o `syncToken` uma vez no mount, e a callable de registro
//   exige login — antes dele a chamada falha e só vai para o console (achado
//   da #926; ver `components/WebPushBootstrap.tsx`).
// Origem: sem issue — commit 4dbd8df25, 2026-04-26 (scaffolding do admin).
// Espera a âncora de hora do servidor antes do 1º render (#1010, auditoria global 2): estado
// inicial congelado no render nasce com a hora certa. Offline/lento: sobe em 2 s com a faixa.
renderAfterServerClock(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CloudscapeI18n>
              <RouterProvider router={router} />
              <AppVersionGate />
              <InstallPromptI18n />
              <WebPushBootstrap />
            </CloudscapeI18n>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
});

// SW do shell PWA — só em prod (em dev o Vite já recarrega ao salvar
// e o SW só atrapalharia com cache stale do shell).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((err: unknown) => {
        console.warn('admin/main: service worker registration failed', err);
      });
  });
}
